import {
  ALERT_SEVERITIES, ALERT_SITES,
  type AlertListSort, type AlertSite, type TrendDimension, type TrendGrain,
} from './alertTypes'

/**
 * The alert report's filter state. The URL is the single source of truth for it
 * (`filtersFromSearch` / `filtersToSearch` round-trip through `useSearchParams`), so a
 * pasted link restores the exact view -- and nothing here is ever held in component
 * state that could drift from what the address bar says. Pure module: no React, no fetch.
 */

export type AlertView = 'overview' | 'drivers' | 'vehicles' | 'locations' | 'daynight' | 'trends' | 'list'
export type AlertPreset = 'today' | 'yesterday' | '7d' | '30d' | 'custom'
export type ShiftFilter = '' | 'day' | 'night'

export interface AlertFilters {
  site: AlertSite
  view: AlertView
  preset: AlertPreset
  /** epoch ms, inclusive */
  from: number
  /** epoch ms, exclusive */
  to: number
  severity: string[]
  kind: string[]
  driver: string
  vehicle: string
  hub: string
  shift: ShiftFilter
  grain: TrendGrain
  dimension: TrendDimension
  page: number
  sort: AlertListSort
  /** Selected alert_id -> the trip drawer is open on it. */
  alert: string | null
}

export const ALERT_VIEWS: readonly AlertView[] = [
  'overview', 'drivers', 'vehicles', 'locations', 'daynight', 'trends', 'list',
]
const PRESETS: readonly AlertPreset[] = ['today', 'yesterday', '7d', '30d', 'custom']
const GRAINS: readonly TrendGrain[] = ['day', 'week', 'hour', 'dow_hour']
const DIMENSIONS: readonly TrendDimension[] = ['kind', 'severity', 'driver', 'vehicle', 'location', 'shift']
const SORTS: readonly AlertListSort[] = ['ts_desc', 'ts_asc', 'severity']

export const DEFAULT_VIEW: AlertView = 'overview'
export const DEFAULT_PRESET: AlertPreset = '7d'
export const DEFAULT_GRAIN: TrendGrain = 'day'
export const DEFAULT_DIMENSION: TrendDimension = 'kind'
export const DEFAULT_SORT: AlertListSort = 'ts_desc'
/** Server page size for the alert list (the /list endpoint's own default and ceiling is
 *  100 / 500 -- one page of 100 keeps the table under the row-animation threshold). */
export const LIST_PAGE_SIZE = 100

/** IST is a fixed UTC+5:30 with no DST, so day boundaries are plain arithmetic -- no
 *  Intl round trip, and identical on every viewer's machine regardless of its own zone. */
const IST_OFFSET_MS = 330 * 60 * 1000
const DAY_MS = 24 * 60 * 60 * 1000

/** Midnight IST of the IST calendar day containing `ms`, as epoch ms. */
export function istDayStart(ms: number): number {
  return Math.floor((ms + IST_OFFSET_MS) / DAY_MS) * DAY_MS - IST_OFFSET_MS
}

/** The [from, to) window a preset means, anchored on IST day boundaries. '7d'/'30d' are
 *  "the last N calendar days including today", so the window always ends at tomorrow
 *  00:00 IST -- a report that stopped at `now` would show a partial today that silently
 *  grows between two reloads. 'custom' has no implied window and returns null. */
export function presetWindow(preset: AlertPreset, now: number = Date.now()): { from: number; to: number } | null {
  const today = istDayStart(now)
  switch (preset) {
    case 'today': return { from: today, to: today + DAY_MS }
    case 'yesterday': return { from: today - DAY_MS, to: today }
    case '7d': return { from: today - 6 * DAY_MS, to: today + DAY_MS }
    case '30d': return { from: today - 29 * DAY_MS, to: today + DAY_MS }
    case 'custom': return null
  }
}

function oneOf<T extends string>(allowed: readonly T[], raw: string | null, fallback: T): T {
  return raw !== null && (allowed as readonly string[]).includes(raw) ? (raw as T) : fallback
}

function csv(raw: string | null): string[] {
  if (!raw) return []
  // Dedupe + drop blanks so `?kind=A,,A` doesn't send a malformed list to the API.
  return [...new Set(raw.split(',').map((s) => s.trim()).filter(Boolean))]
}

function text(raw: string | null, max = 80): string {
  return (raw ?? '').trim().slice(0, max)
}

function parseIsoMs(raw: string | null): number | null {
  if (!raw) return null
  const ms = new Date(raw).getTime()
  return Number.isFinite(ms) ? ms : null
}

export interface FilterDefaults {
  /** The default site: the first granted one. Also the site an unknown `?site=` falls to. */
  site: AlertSite
  /** The sites this user may pick; a `?site=` outside it is coerced to `site`. */
  sites?: readonly AlertSite[]
  now?: number
}

export function filtersFromSearch(sp: URLSearchParams, defaults: FilterDefaults): AlertFilters {
  const allowedSites = defaults.sites ?? ALERT_SITES
  const rawSite = sp.get('site')
  const site: AlertSite = rawSite !== null && (allowedSites as readonly string[]).includes(rawSite)
    ? (rawSite as AlertSite)
    : defaults.site

  let preset = oneOf(PRESETS, sp.get('preset'), DEFAULT_PRESET)
  let from: number
  let to: number
  const customFrom = parseIsoMs(sp.get('from'))
  const customTo = parseIsoMs(sp.get('to'))
  // Explicit from/to in the URL mean custom even when `preset` is missing -- a link
  // someone hand-edited to a window should show that window, not silently the default.
  if (customFrom !== null && customTo !== null && customTo > customFrom) {
    preset = 'custom'
    from = customFrom
    to = customTo
  } else {
    if (preset === 'custom') preset = DEFAULT_PRESET
    const w = presetWindow(preset, defaults.now)!
    from = w.from
    to = w.to
  }

  const page = Number.parseInt(sp.get('page') ?? '0', 10)
  const alert = text(sp.get('alert'), 64)

  return {
    site,
    view: oneOf(ALERT_VIEWS, sp.get('view'), DEFAULT_VIEW),
    preset,
    from,
    to,
    severity: csv(sp.get('severity')).filter((s) => (ALERT_SEVERITIES as readonly string[]).includes(s)),
    kind: csv(sp.get('kind')),
    driver: text(sp.get('driver')),
    vehicle: text(sp.get('vehicle')),
    hub: text(sp.get('hub')),
    shift: oneOf(['', 'day', 'night'] as const, sp.get('shift'), ''),
    grain: oneOf(GRAINS, sp.get('grain'), DEFAULT_GRAIN),
    dimension: oneOf(DIMENSIONS, sp.get('dimension'), DEFAULT_DIMENSION),
    page: Number.isFinite(page) && page > 0 ? page : 0,
    sort: oneOf(SORTS, sp.get('sort'), DEFAULT_SORT),
    alert: alert || null,
  }
}

/** Inverse of filtersFromSearch. Defaults are omitted so the address bar stays short
 *  and a fresh visit's URL is literally `/reports/alerts`. */
export function filtersToSearch(f: AlertFilters, defaults: Pick<FilterDefaults, 'site'>): URLSearchParams {
  const sp = new URLSearchParams()
  if (f.view !== DEFAULT_VIEW) sp.set('view', f.view)
  if (f.site !== defaults.site) sp.set('site', f.site)
  if (f.preset === 'custom') {
    sp.set('from', new Date(f.from).toISOString())
    sp.set('to', new Date(f.to).toISOString())
  } else if (f.preset !== DEFAULT_PRESET) {
    sp.set('preset', f.preset)
  }
  if (f.severity.length) sp.set('severity', f.severity.join(','))
  if (f.kind.length) sp.set('kind', f.kind.join(','))
  if (f.driver) sp.set('driver', f.driver)
  if (f.vehicle) sp.set('vehicle', f.vehicle)
  if (f.hub) sp.set('hub', f.hub)
  if (f.shift) sp.set('shift', f.shift)
  if (f.grain !== DEFAULT_GRAIN) sp.set('grain', f.grain)
  if (f.dimension !== DEFAULT_DIMENSION) sp.set('dimension', f.dimension)
  if (f.page > 0) sp.set('page', String(f.page))
  if (f.sort !== DEFAULT_SORT) sp.set('sort', f.sort)
  if (f.alert) sp.set('alert', f.alert)
  return sp
}

/** The common query every aggregation endpoint takes (plan: same order on every SQL
 *  function). from/to go as UTC ISO -- the API parses any offset; only a NAIVE string
 *  would be read as IST, and we never send one. */
export function toApiParams(f: AlertFilters): URLSearchParams {
  const sp = new URLSearchParams()
  sp.set('site', f.site)
  sp.set('from', new Date(f.from).toISOString())
  sp.set('to', new Date(f.to).toISOString())
  if (f.severity.length) sp.set('severity', f.severity.join(','))
  if (f.kind.length) sp.set('kind', f.kind.join(','))
  if (f.driver) sp.set('driver', f.driver)
  if (f.vehicle) sp.set('vehicle_id', f.vehicle)
  if (f.hub) sp.set('hub', f.hub)
  if (f.shift) sp.set('shift', f.shift)
  return sp
}

/** /facets takes only site + window: the facet counts describe the whole window so the
 *  pickers never shrink to just whatever survives the current selection. */
export function toFacetParams(f: AlertFilters): URLSearchParams {
  const sp = new URLSearchParams()
  sp.set('site', f.site)
  sp.set('from', new Date(f.from).toISOString())
  sp.set('to', new Date(f.to).toISOString())
  return sp
}

/** True when any narrowing filter (not the site/window) is set -- what separates "this
 *  filter matched nothing" from "there is no data in this window" in every empty state. */
export function isNarrowed(f: AlertFilters): boolean {
  return Boolean(
    f.severity.length || f.kind.length || f.driver || f.vehicle || f.hub || f.shift,
  )
}

/** The narrowing filters reset; site, window, view and the view-local knobs survive. */
export function clearNarrowing(f: AlertFilters): AlertFilters {
  return { ...f, severity: [], kind: [], driver: '', vehicle: '', hub: '', shift: '', page: 0, alert: null }
}

/** Keys whose change invalidates the current list page (anything that changes the row
 *  set), so the shell can send `page` back to 0 on them. */
export const PAGE_RESETTING_KEYS: readonly (keyof AlertFilters)[] = [
  'site', 'preset', 'from', 'to', 'severity', 'kind', 'driver', 'vehicle', 'hub', 'shift', 'sort',
]

const IST_DAY_FMT = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric',
})
const IST_MIN_FMT = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
})

/** Epoch ms -> 'YYYY-MM-DDTHH:mm' in IST with no offset: the shape the OLD filtered
 *  report routes (alert-profile/noise/repeat-offenders) read as IST-naive, i.e. what the
 *  pre-rebuild datetime-local inputs sent from an IST browser. */
export function toIstNaive(ms: number): string {
  const d = new Date(ms + IST_OFFSET_MS)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`
}

export const SITE_LABEL: Record<AlertSite, string> = {
  ultratech: 'Ultratech',
  wondercement: 'Wonder Cement',
}

/** Human window for subtitles and export headers: "Ultratech · 01 Oct 2026 – 07 Oct 2026 IST".
 *  A `to` that sits exactly on an IST midnight is shown as the previous day (the window is
 *  exclusive, and "to 08 Oct" would read as including the 8th). */
export function windowLabel(f: AlertFilters): string {
  const onMidnight = istDayStart(f.to) === f.to
  const fromLabel = IST_DAY_FMT.format(new Date(f.from))
  const toLabel = onMidnight ? IST_DAY_FMT.format(new Date(f.to - 1)) : IST_MIN_FMT.format(new Date(f.to))
  const fromText = onMidnight && istDayStart(f.from) === f.from ? fromLabel : IST_MIN_FMT.format(new Date(f.from))
  return `${SITE_LABEL[f.site]} · ${fromText === toLabel ? fromText : `${fromText} – ${toLabel}`} IST`
}
